import React, { useEffect, useState } from 'react';
import { Box } from '@mui/material';
import Button from 'components/Button/Index';
import { TFunction } from 'i18next';
import { SkillService } from 'services/skills/skillService';
import { CandidateService } from 'services/candidate/candidate';
import { uniqSkills } from 'utils/normalizeSkillList';
import { SkillSection } from './SkillSection';

import './candidate-skills.scss';

export type CandidateSkillsFormProps = {
  t: TFunction;
  candidateId: number;
  initialHardSkills: string[];
  initialSoftSkills: string[];
  existingHardSkills: string[];
  existingSoftSkills: string[];
  showLegend?: boolean;
  /** CV upload uses resume copy; profile tab uses "New skills". */
  legendVariant?: 'cv' | 'profile';
  onClose: () => void;
  onSaved?: () => void;
};

const CandidateSkillsForm: React.FC<CandidateSkillsFormProps> = ({
  t,
  candidateId,
  initialHardSkills,
  initialSoftSkills,
  existingHardSkills,
  existingSoftSkills,
  showLegend = false,
  legendVariant = 'cv',
  onClose,
  onSaved
}) => {
  const newSkillsLegendLabel =
    legendVariant === 'profile'
      ? t('New skills')
      : t('Skills found in your resume');
  const [hardSkills, setHardSkills] = useState<string[]>(initialHardSkills);
  const [softSkills, setSoftSkills] = useState<string[]>(initialSoftSkills);
  const [isSaving, setIsSaving] = useState(false);

  useEffect(() => {
    setHardSkills(initialHardSkills);
    setSoftSkills(initialSoftSkills);
  }, [initialHardSkills, initialSoftSkills]);

  const handleSave = async () => {
    setIsSaving(true);
    try {
      await SkillService.addGeneralSkill(
        candidateId,
        uniqSkills(hardSkills),
        uniqSkills(softSkills)
      );
      await CandidateService.getCandidate(candidateId);
      onSaved?.();
      onClose();
    } catch (error) {
      console.error('Error saving skills:', error);
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <Box className="cv-skills-review">
      <SkillSection
        label={t('Hard Skills')}
        placeholder={t('Add tags...')}
        value={hardSkills}
        savedSkills={existingHardSkills}
        onChange={setHardSkills}
      />
      <SkillSection
        label={t('Soft Skills')}
        placeholder={t('Add tags...')}
        value={softSkills}
        savedSkills={existingSoftSkills}
        onChange={setSoftSkills}
      />
      <p className="cv-skills-review__helper">
        {t(
          'For each skill, press Enter to add a tag. Remove any skill you do not want to keep.'
        )}
      </p>
      {showLegend && (
        <div className="cv-skills-review__legend">
          <span className="cv-skills-review__legend-item">
            <span className="cv-skills-review__legend-swatch cv-skills-review__legend-swatch--saved" />
            {t('Skills already on your profile')}
          </span>
          <span className="cv-skills-review__legend-item">
            <span className="cv-skills-review__legend-swatch cv-skills-review__legend-swatch--new" />
            {newSkillsLegendLabel}
          </span>
        </div>
      )}
      <Button
        onClick={handleSave}
        t={t}
        type="submit"
        title="Save"
        className="save-button"
        isLoading={isSaving}
      />
    </Box>
  );
};

export default CandidateSkillsForm;
